import { useEffect, useState } from 'react'
import { Globe, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useAttachFromGlobalMaterial, useCreateMaterial } from '@/features/projects/use-catalog'
import { useAttachMaterial } from '@/features/projects/use-step-attachments'
import { useGlobalCatalogSearch } from '@/features/catalog/use-global-catalog'
import type { CatalogMaterial, GlobalCatalogMaterial } from '@/api/types'

interface MaterialPickerProps {
  stepId: string
  projectId: string
  catalog: CatalogMaterial[]
  attachedIds: string[]
}

/**
 * Phase 15 added two new modes for the global-catalog flow: `searchGlobal` (search input +
 * result list across the shared catalog) and `attachFromGlobal` (asks for quantity before
 * calling POST /catalog/materials/from-global followed by the existing attach mutation).
 */
type Mode =
  | { kind: 'list' }
  | { kind: 'attachExisting'; material: CatalogMaterial }
  | { kind: 'createNew'; name: string }
  | { kind: 'searchGlobal' }
  | { kind: 'attachFromGlobal'; global: GlobalCatalogMaterial }

const dollarsToCents = (input: string): number | null => {
  const trimmed = input.trim()
  if (!trimmed) return null
  const num = Number(trimmed)
  if (!Number.isFinite(num) || num < 0) return null
  return Math.round(num * 100)
}

export const MaterialPicker = ({
  stepId,
  projectId,
  catalog,
  attachedIds,
}: MaterialPickerProps) => {
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<Mode>({ kind: 'list' })
  const [search, setSearch] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [unit, setUnit] = useState('ea')
  const [priceDollars, setPriceDollars] = useState('0')

  const create = useCreateMaterial(projectId)
  const attachFromGlobal = useAttachFromGlobalMaterial(projectId)
  const attach = useAttachMaterial(projectId)
  const [globalQuery, setGlobalQuery] = useState('')
  const globalSearch = useGlobalCatalogSearch('MATERIAL', globalQuery, mode.kind === 'searchGlobal')

  useEffect(() => {
    if (!open) {
      setMode({ kind: 'list' })
      setSearch('')
      setQuantity('1')
      setUnit('ea')
      setPriceDollars('0')
      setGlobalQuery('')
    }
  }, [open])

  const available = catalog.filter((m) => !attachedIds.includes(m.id))
  const trimmed = search.trim()
  const exactMatch = available.find((m) => m.name.toLowerCase() === trimmed.toLowerCase())

  const submitAttachExisting = async (material: CatalogMaterial) => {
    const qty = Number(quantity)
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error('Quantity must be greater than 0')
      return
    }
    try {
      await attach.mutateAsync({
        stepId,
        data: { catalogMaterialId: material.id, quantity: qty },
      })
      setOpen(false)
    } catch {
      toast.error('Failed to attach material')
    }
  }

  const submitCreateAndAttach = async () => {
    const name = (mode.kind === 'createNew' ? mode.name : '').trim()
    if (!name) {
      toast.error('Name is required')
      return
    }
    if (!unit.trim()) {
      toast.error('Unit is required')
      return
    }
    const cents = dollarsToCents(priceDollars)
    if (cents === null) {
      toast.error('Price must be a non-negative number')
      return
    }
    const qty = Number(quantity)
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error('Quantity must be greater than 0')
      return
    }
    try {
      const material = await create.mutateAsync({
        name,
        unit: unit.trim(),
        unitPriceCents: cents,
      })
      await attach.mutateAsync({
        stepId,
        data: { catalogMaterialId: material.id, quantity: qty },
      })
      setOpen(false)
    } catch {
      toast.error('Failed to create + attach material')
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1">
          <Plus className="h-3 w-3" />
          Material
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        {mode.kind === 'list' && (
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search or add a material…"
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {available.length === 0 && !trimmed && (
                <CommandEmpty>No materials yet. Type a name to create one.</CommandEmpty>
              )}
              {available.length > 0 && (
                <CommandGroup heading="Catalog">
                  {available
                    .filter((m) =>
                      trimmed === '' ? true : m.name.toLowerCase().includes(trimmed.toLowerCase())
                    )
                    .map((m) => (
                      <CommandItem
                        key={m.id}
                        value={m.name}
                        onSelect={() => setMode({ kind: 'attachExisting', material: m })}
                      >
                        <span className="flex-1">{m.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ${(m.unitPriceCents / 100).toFixed(2)} / {m.unit}
                        </span>
                      </CommandItem>
                    ))}
                </CommandGroup>
              )}
              {trimmed && !exactMatch && (
                <CommandGroup heading="Add new">
                  <CommandItem
                    value={`__create__${trimmed}`}
                    onSelect={() => setMode({ kind: 'createNew', name: trimmed })}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create &ldquo;{trimmed}&rdquo;
                  </CommandItem>
                </CommandGroup>
              )}
              <CommandGroup heading="Global catalog">
                <CommandItem
                  value="__search-global__"
                  onSelect={() => {
                    setGlobalQuery(trimmed)
                    setMode({ kind: 'searchGlobal' })
                  }}
                >
                  <Globe className="mr-2 h-4 w-4" />
                  Search shared catalog
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        )}

        {mode.kind === 'searchGlobal' && (
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search the shared catalog…"
              value={globalQuery}
              onValueChange={setGlobalQuery}
            />
            <CommandList>
              {globalSearch.isLoading && <CommandEmpty>Searching…</CommandEmpty>}
              {!globalSearch.isLoading && (globalSearch.data?.items.length ?? 0) === 0 && (
                <CommandEmpty>No matches in the shared catalog.</CommandEmpty>
              )}
              {globalSearch.data && globalSearch.data.items.length > 0 && (
                <CommandGroup heading="Shared catalog">
                  {globalSearch.data.items.map((g) => (
                    <CommandItem
                      key={g.id}
                      value={g.id}
                      onSelect={() => setMode({ kind: 'attachFromGlobal', global: g })}
                    >
                      <span className="flex-1">{g.name}</span>
                      <span className="text-xs text-muted-foreground">{g.unit}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
            <div className="border-t border-border p-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setMode({ kind: 'list' })}
              >
                Back
              </Button>
            </div>
          </Command>
        )}

        {mode.kind === 'attachFromGlobal' && (
          <form
            onSubmit={async (e) => {
              e.preventDefault()
              const cents = dollarsToCents(priceDollars)
              if (cents === null) {
                toast.error('Price must be a non-negative number')
                return
              }
              const qty = Number(quantity)
              if (!Number.isFinite(qty) || qty <= 0) {
                toast.error('Quantity must be greater than 0')
                return
              }
              try {
                const created = await attachFromGlobal.mutateAsync({
                  globalCatalogMaterialId: mode.global.id,
                  unitPriceCents: cents,
                })
                await attach.mutateAsync({
                  stepId,
                  data: { catalogMaterialId: created.id, quantity: qty },
                })
                setOpen(false)
              } catch {
                toast.error('Failed to attach from shared catalog')
              }
            }}
            className="space-y-3 p-3"
          >
            <div>
              <p className="text-sm font-medium">{mode.global.name}</p>
              <p className="text-xs text-muted-foreground">Shared catalog · {mode.global.unit}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="price-from-global">Price (USD)</Label>
                <Input
                  id="price-from-global"
                  type="number"
                  step="0.01"
                  min="0"
                  value={priceDollars}
                  onChange={(e) => setPriceDollars(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="qty-from-global">Quantity</Label>
                <Input
                  id="qty-from-global"
                  type="number"
                  step="0.01"
                  min="0"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setMode({ kind: 'searchGlobal' })}
              >
                Back
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={attachFromGlobal.isPending || attach.isPending}
              >
                Use + attach
              </Button>
            </div>
          </form>
        )}

        {mode.kind === 'attachExisting' && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              void submitAttachExisting(mode.material)
            }}
            className="space-y-3"
          >
            <div>
              <p className="text-sm font-medium">{mode.material.name}</p>
              <p className="text-xs text-muted-foreground">
                ${(mode.material.unitPriceCents / 100).toFixed(2)} / {mode.material.unit}
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="qty-existing">Quantity</Label>
              <Input
                id="qty-existing"
                autoFocus
                type="number"
                step="0.01"
                min="0"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setMode({ kind: 'list' })}
              >
                Back
              </Button>
              <Button type="submit" size="sm" disabled={attach.isPending}>
                Attach
              </Button>
            </div>
          </form>
        )}

        {mode.kind === 'createNew' && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              void submitCreateAndAttach()
            }}
            className="space-y-3"
          >
            <div>
              <p className="text-sm font-medium">New material</p>
              <p className="text-xs text-muted-foreground">{mode.name}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label htmlFor="unit">Unit</Label>
                <Input
                  id="unit"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  placeholder="ft, ea, lb"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="price">Price (USD)</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={priceDollars}
                  onChange={(e) => setPriceDollars(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="qty-new">Quantity</Label>
              <Input
                id="qty-new"
                type="number"
                step="0.01"
                min="0"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setMode({ kind: 'list' })}
              >
                Back
              </Button>
              <Button type="submit" size="sm" disabled={create.isPending || attach.isPending}>
                Create + attach
              </Button>
            </div>
          </form>
        )}
      </PopoverContent>
    </Popover>
  )
}
